// User related models - converted from ActionScript
import { CurrencyInfo } from './app.model';

export interface ImageInfo {
  id?: number;
  url?: string;
  thumb?: string;
  width?: number;
  height?: number;
  noCache?: string;
}

export interface BillingAddress {
  id?: number;
  name?: string;
  address?: string;
  postalCode?: string;
  city?: number;
  state?: number;
  country?: number;
  vatNumber?: string;
}

export interface LoginCredentials {
  username: string;
  password: string;
  trial: number;
  update: number;
  sessionId?: string;
}

/**
 * One plafond voucher row from GET /users/balance. The endpoint returns the
 * customer's FULL history — spent and expired rows included — so filter for
 * display. Amounts are raw numbers (not rounded to cents); legacy login
 * payloads may still carry them as strings, hence the unions.
 */
export interface UserBalanceInfo {
  voucherId: string;
  /** Translated name of the product the plafond was bought as. Can be an
   * empty string (unresolvable product, or BR prepaid-register rows) —
   * render a fallback instead of assuming it's populated. */
  name: string;
  totalAmount: number | string;
  usedAmount: number | string;
  remainingAmount: number;
  /** YYYY-MM-DD date-only, or null. Already includes any extension
   * previously granted. */
  expiration?: string | null;
  /** Campaign-level flag: extensions permitted AT ALL — not "an offer is
   * available right now". Drive extension UI off extendProduct !== null. */
  allowExtension?: boolean;
  /** Concrete extension offer (a normal product payload), or null. Non-null
   * only for expired plafonds with remaining balance; finishOptions[0]
   * .finishes comes already filtered to the lengths valid for THIS plafond —
   * never merge or cache them across rows. */
  extendProduct?: any;
}

export interface UserInfo {
  id: number;
  typeId: number;
  company?: string;
  /** Brand/studio name shown to end clients (share emails, guest preview,
   * galleries). `company` stays the fiscal name. Key ABSENT (not just empty)
   * means the backend doesn't serialize it yet — commercial-name UI surfaces
   * are presence-gated on it and stay hidden until the API ships the field. */
  commercialName?: string;
  name?: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  nif?: string;
  avatar?: ImageInfo;
  currencyId: number;
  displayCurrencyId: number;
  /** The user's full currency object as sent by the backend. Authoritative —
   * carries code/symbol/separators even when the currency isn't in the global
   * /common/currencies list (e.g. USD). Prefer this over re-deriving from
   * currencyId against that list. */
  currency?: CurrencyInfo;
  discountType?: string;
  vatTab?: number;
  vatRegime?: string;
  recargoTax?: number;
  priceTab?: number;
  credentials: LoginCredentials;
  token?: string;
  showPrice?: number;
  paymentPermissions?: number;
  allowOrderWithoutPrice?: number;
  allowOrdersGrouping: boolean;
  allowOrderIssues: boolean;
  allowGalleries?: boolean;
  billingAddress?: BillingAddress;
  /** Client's HOME country (REST /users/me `countryId` = customers.country_id).
   *  This IS the legacy billingAddress.country (1=PT, 29=BR, 62=ES, 69=FR,
   *  99=IT, 51=DE, 35=CA, 211=USA) — key country-specific CONTENT (price
   *  lists, banners, category gates) on this field. */
  countryId?: number;
  /** Billing/TAX country (REST /users/me `billingCountryId` =
   *  customers.billing_country_id) — the invoicing side, NOT the legacy
   *  billingAddress.country despite the name. Can differ from countryId (an
   *  Italian client invoiced by the PT entity has countryId=99,
   *  billingCountryId=1). Fiscal/VAT logic only — never content selection. */
  billingCountryId?: number;
  addresses?: Address[];
  addressesPermissionType: number;
  shipmentTypeRef?: string;
  /** Backend sends brands as objects ({ id, name }, e.g. { name: 'DREAMBOOKSPRO' });
   * older payloads sent plain strings. GlobalsService.setBrands accepts both. */
  brands: Array<string | { id?: number; name?: string }>;
  partnerId: number;
  selected?: boolean;
  origin: number;
  location: number;
  balance?: UserBalanceInfo[];
  points: number;
  hasCollectionRequest: boolean;
  /** Feature flag: gates the Album Design area (drafts with draftKind=1). From user:read. */
  hasAlbumDesign?: boolean;
  /** Feature flag: the account may manage team members (customer sub-users).
   * Gates the "Team" management section — customer sessions only. From user:read. */
  allowCustomerUsers?: boolean;
  gdprEligible: boolean;
  acceptedGDPR: boolean;
  vatAlert: boolean;
  preferences?: UserPreferences;
}

export interface Address {
  id: number;
  address: string;
  door: string;
  floor: string;
  local: string;
  postalCode: string;
  city: number;
  state: number;
  country: number;
  mainAddress: number;
  selected?: boolean;
  allowSelect?: boolean;
  allowRemove?: boolean;
  allowEdit?: boolean;
  saved?: boolean;
}

export interface ShipmentAddress extends Address {
  name?: string;
  phone?: string;
  company?: string;
  observation?: string;
}

export interface ShareUser {
  id?: number;
  email: string;
  name: string;
  avatar?: ImageInfo;
}

export enum AddressesPermissionType {
  NORMAL = 0,
  READ_ONLY = 1
}

export enum UserBrand {
  DREAMBOOKSPRO = 'dreambookspro',
  ONELABPRO = 'onelabpro',
  LFMPRO = 'lfmpro'
}

export interface UserPreferences {
  winX?: number;
  winY?: number;
  winWidth?: number;
  winHeight?: number;
  winMaximized?: boolean;
  locale?: string;
  localUser?: ShareUser;
  localUserCredentials?: LoginCredentials;
  localUserKeepSession?: boolean;
}
